import React, { useState, useCallback } from 'react';
import { 
  Button, 
  Textarea, 
  Card, 
  CardBody, 
  CardHeader,
  Chip,
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  useDisclosure,
  Progress,
  Badge
} from '@heroui/react';

/**
 * NextStepsStrategist - A kanban-based detective analysis tool
 * 
 * This component analyzes case reports from a police detective perspective
 * to determine what has been done, what's in progress, and what needs to happen next.
 * Displays results in an interactive kanban board format.
 */

// Kanban column definitions
const KANBAN_COLUMNS = [
  {
    id: 'completed',
    title: 'Completed Actions',
    subtitle: 'What has been done',
    color: 'success',
    icon: '✅',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  },
  {
    id: 'inProgress',
    title: 'In Progress',
    subtitle: 'Current ongoing work',
    color: 'warning',
    icon: '🔄',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  {
    id: 'nextSteps',
    title: 'Next Steps',
    subtitle: 'Priority actions needed',
    color: 'primary',
    icon: '📋',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  {
    id: 'questions',
    title: 'Questions to Ask',
    subtitle: 'Investigation inquiries',
    color: 'secondary',
    icon: '❓',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  {
    id: 'interviews',
    title: 'People to Interview',
    subtitle: 'Witness/suspect contacts',
    color: 'default',
    icon: '👥',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200'
  },
  {
    id: 'locations',
    title: 'Places to Visit',
    subtitle: 'Scene/location checks',
    color: 'danger',
    icon: '📍',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  }
];

// Priority levels for tasks
const PRIORITY_LEVELS = {
  high: { color: 'danger', label: 'High', icon: '🔥' },
  medium: { color: 'warning', label: 'Medium', icon: '⚡' },
  low: { color: 'success', label: 'Low', icon: '📌' }
};

/**
 * EditTaskModal Component - Modal for editing task details
 */
const EditTaskModal = ({ isOpen, onOpenChange, task, onUpdate, onCancel }) => {
  const [editedTask, setEditedTask] = useState(task || {});

  // Update editedTask when task prop changes
  React.useEffect(() => {
    if (task) {
      setEditedTask({ ...task });
    }
  }, [task]);

  const handleSave = useCallback(() => {
    onUpdate(editedTask);
    onOpenChange(false);
  }, [editedTask, onUpdate, onOpenChange]);

  if (!task) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={onOpenChange} 
      size="2xl" 
      scrollBehavior="inside"
      placement="center"
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>
              <h3 className="text-xl font-semibold">Edit Task</h3>
            </ModalHeader>
            <ModalBody className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <Textarea
                  value={editedTask?.description || ''}
                  onChange={(e) => setEditedTask(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Task description..."
                  minRows={2}
                  maxRows={4}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Details
                </label>
                <Textarea
                  value={editedTask?.details || ''}
                  onChange={(e) => setEditedTask(prev => ({ ...prev, details: e.target.value }))}
                  placeholder="Additional details..."
                  minRows={3}
                  maxRows={6}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority
                  </label>
                  <select
                    value={editedTask?.priority || 'medium'}
                    onChange={(e) => setEditedTask(prev => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="low">Low Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="high">High Priority</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assigned To
                  </label>
                  <input
                    type="text"
                    value={editedTask?.assignedTo || ''}
                    onChange={(e) => setEditedTask(prev => ({ ...prev, assignedTo: e.target.value }))}
                    placeholder="Officer/Detective name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deadline
                </label>
                <input
                  type="date"
                  value={editedTask?.deadline || ''}
                  onChange={(e) => setEditedTask(prev => ({ ...prev, deadline: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="urgent-checkbox"
                  checked={editedTask?.urgent || false}
                  onChange={(e) => setEditedTask(prev => ({ ...prev, urgent: e.target.checked }))}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="urgent-checkbox" className="text-sm font-medium text-gray-700">
                  Mark as urgent
                </label>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button 
                color="danger" 
                variant="light" 
                onPress={onCancel}
              >
                Cancel
              </Button>
              <Button 
                color="primary" 
                onPress={handleSave}
              >
                Save Changes
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

/**
 * KanbanCard Component - Individual task card
 */
const KanbanCard = ({ task, onEdit, onDelete }) => {
  const priority = PRIORITY_LEVELS[task.priority] || PRIORITY_LEVELS.medium;
  
  return (
    <Card className="mb-3 hover:shadow-md transition-shadow cursor-pointer" isPressable>
      <CardBody className="p-3">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-2">
            <Chip 
              size="sm" 
              color={priority.color} 
              variant="flat"
              className="text-xs"
            >
              {priority.icon} {priority.label}
            </Chip>
            {task.urgent && (
              <Badge content="!" color="danger" size="sm">
                <div className="w-1"></div>
              </Badge>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(task);
              }}
              className="min-w-0 w-6 h-6"
              aria-label="Edit task"
            >
              ✏️
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(task.id);
              }}
              className="min-w-0 w-6 h-6"
              aria-label="Delete task"
            >
              🗑️
            </Button>
          </div>
        </div>
        
        <p className="text-sm text-gray-700 mb-2">{task.description}</p>
        
        {task.details && (
          <p className="text-xs text-gray-500 mb-2">{task.details}</p>
        )}
        
        {task.assignedTo && task.assignedTo !== 'Unknown' && (
          <Chip size="sm" variant="bordered" className="text-xs">
            👤 {task.assignedTo}
          </Chip>
        )}
        
        {task.deadline && (
          <div className="mt-2">
            <Chip size="sm" color="default" variant="flat" className="text-xs">
              📅 {task.deadline}
            </Chip>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

/**
 * KanbanColumn Component - Column container
 */
const KanbanColumn = ({ column, tasks, onEditTask, onDeleteTask }) => {
  const columnTasks = tasks.filter(task => task.category === column.id);
  
  return (
          <div className={`flex-1 min-w-80 ${column.bgColor} ${column.borderColor} border-2 rounded-lg p-4 min-h-[600px]`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-2xl">{column.icon}</span>
              {column.title}
            </h3>
            <p className="text-sm text-gray-600">{column.subtitle}</p>
          </div>
          <Badge content={columnTasks.length} color={column.color} size="lg">
            <div className="w-2"></div>
          </Badge>
        </div>
        
        <div className="space-y-2 max-h-[520px] overflow-y-auto">
          {columnTasks.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <p className="text-sm">No items yet</p>
              <p className="text-xs text-gray-400 mt-2">Items will appear here after analysis</p>
            </div>
          ) : (
            columnTasks.map(task => (
              <KanbanCard
                key={task.id}
                task={task}
                onEdit={onEditTask}
                onDelete={onDeleteTask}
              />
            ))
          )}
        </div>
      </div>
  );
};

/**
 * Main NextStepsStrategist Component
 */
const NextStepsStrategist = () => {
  // Component state
  const [reportText, setReportText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(null);
  
  // Modal controls
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  
  // Edit task state
  const [editingTask, setEditingTask] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // File input ref for import functionality
  const fileInputRef = React.useRef(null);

  /**
   * Analyze case report and generate strategic next steps
   */
  const analyzeCase = useCallback(async () => {
    if (!reportText.trim()) {
      setError('Please enter a case report to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      // Call AI service for next steps analysis
      const response = await fetch(
        process.env.NODE_ENV === 'development' 
          ? 'http://localhost:3001/api/ai/next-steps' 
          : '/api/ai/next-steps',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reportText }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to analyze case');
      }

      const result = await response.json();
      
      if (result.error) {
        setError(result.error);
        return;
      }

      setAnalysis(result);
      
      // Transform AI analysis into kanban tasks
      const newTasks = [];
      let taskId = 1;

      // Process each category
      result.completed?.forEach(item => {
        newTasks.push({
          id: taskId++,
          category: 'completed',
          description: item.action,
          details: item.details,
          priority: 'low',
          assignedTo: item.officer || '' // Empty string instead of 'Unknown'
        });
      });

      result.inProgress?.forEach(item => {
        newTasks.push({
          id: taskId++,
          category: 'inProgress',
          description: item.action,
          details: item.status || item.details,
          priority: item.priority || 'medium',
          assignedTo: item.officer || '', // Ensure empty string if no officer
          deadline: item.deadline
        });
      });

      result.nextSteps?.forEach(item => {
        newTasks.push({
          id: taskId++,
          category: 'nextSteps',
          description: item.action,
          details: item.rationale,
          priority: item.priority || 'high',
          urgent: item.urgent || false
        });
      });

      result.questions?.forEach(item => {
        newTasks.push({
          id: taskId++,
          category: 'questions',
          description: item.question,
          details: item.context,
          priority: item.priority || 'medium'
        });
      });

      result.interviews?.forEach(item => {
        newTasks.push({
          id: taskId++,
          category: 'interviews',
          description: `Interview ${item.person}`,
          details: item.reason || item.questions?.join('; '),
          priority: item.priority || 'medium'
        });
      });

      result.locations?.forEach(item => {
        newTasks.push({
          id: taskId++,
          category: 'locations',
          description: `Visit ${item.location}`,
          details: item.purpose,
          priority: item.priority || 'medium'
        });
      });

      setTasks(newTasks);
      
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || 'Failed to analyze case');
    } finally {
      setIsAnalyzing(false);
    }
  }, [reportText]);

  /**
   * Edit task - opens edit modal
   */
  const handleEditTask = useCallback((task) => {
    setEditingTask({ ...task });
    setIsEditModalOpen(true);
  }, []);

  /**
   * Update task with edited values
   */
  const handleUpdateTask = useCallback((updatedTask) => {
    setTasks(prev => prev.map(task => 
      task.id === updatedTask.id ? updatedTask : task
    ));
    setEditingTask(null);
    setIsEditModalOpen(false);
  }, []);

  /**
   * Cancel task editing
   */
  const handleCancelEdit = useCallback(() => {
    setEditingTask(null);
    setIsEditModalOpen(false);
  }, []);

  /**
   * Delete task
   */
  const handleDeleteTask = useCallback((taskId) => {
    setTasks(prev => prev.filter(task => task.id !== taskId));
  }, []);

  /**
   * Clear analysis and start over
   */
  const handleClear = useCallback(() => {
    setAnalysis(null);
    setTasks([]);
    setError(null);
  }, []);

  /**
   * Export analysis results as JSON file
   */
  const handleExportToFile = useCallback(() => {
    if (!analysis || tasks.length === 0) {
      setError('No analysis results to export');
      return;
    }

    const exportData = {
      reportText: reportText,
      analysis: analysis,
      tasks: tasks,
      exportDate: new Date().toISOString(),
      version: '1.0',
      toolName: 'NextStepsStrategist'
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `next-steps-analysis-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [analysis, tasks, reportText]);

  /**
   * Import analysis results from JSON file
   */
  const handleImportFromFile = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setError('Please select a valid JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData = JSON.parse(e.target.result);
        
        // Validate imported data structure
        if (!importData.analysis || typeof importData.analysis !== 'object') {
          throw new Error('Invalid file format: missing or invalid analysis object');
        }
        if (!importData.tasks || !Array.isArray(importData.tasks)) {
          throw new Error('Invalid file format: missing or invalid tasks array');
        }

        // Restore state
        setReportText(importData.reportText || '');
        setAnalysis(importData.analysis);
        setTasks(importData.tasks);
        setError(null);
        
      } catch (err) {
        console.error('Import error:', err);
        setError(`Failed to import file: ${err.message}`);
      }
    };
    
    reader.onerror = () => {
      setError('Failed to read file');
    };
    
    reader.readAsText(file);
    
    // Clear the input so the same file can be selected again
    event.target.value = '';
  }, []);

  /**
   * Trigger file import dialog
   */
  const handleImportFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="p-6 max-w-full min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          🕵️ Next Steps Strategist
        </h1>
        <p className="text-gray-600 dark:text-gray-300">
          AI-powered detective assistant that analyzes case reports to identify completed work, 
          ongoing investigations, and strategic next steps for case progression.
        </p>
      </div>

      {/* Input Section */}
      {!analysis && (
        <div className="mb-8">
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Case Report Analysis</h2>
            </CardHeader>
            <CardBody>
              <div className="space-y-6">
                <Textarea
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  placeholder="Paste your case report here for strategic analysis..."
                  minRows={8}
                  maxRows={15}
                  className="w-full"
                />
                
                {error && (
                  <Chip color="danger" variant="flat">
                    ⚠️ {error}
                  </Chip>
                )}
                
                <div className="flex gap-2">
                  <Button
                    onClick={analyzeCase}
                    isDisabled={isAnalyzing || !reportText.trim()}
                    color="primary"
                    size="lg"
                    isLoading={isAnalyzing}
                    className="font-semibold"
                  >
                    {isAnalyzing ? 'Analyzing Case...' : '🔍 Analyze Case'}
                  </Button>
                  <Button
                    onClick={handleImportFile}
                    variant="flat"
                    color="warning"
                    size="lg"
                  >
                    📂 Import Analysis
                  </Button>
                  <Button
                    onClick={onOpen}
                    variant="flat"
                    color="secondary"
                    size="lg"
                  >
                    📖 How It Works
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Loading State */}
      {isAnalyzing && (
        <Card className="mb-8">
          <CardBody>
            <div className="text-center py-12">
              <Progress
                value={33}
                color="primary"
                className="mb-6"
                isIndeterminate
              />
              <h3 className="text-xl font-semibold mb-3">🤖 Detective AI Analyzing...</h3>
              <p className="text-gray-600 text-lg">
                Reviewing case details, identifying completed actions, and strategizing next steps...
              </p>
              <p className="text-gray-500 text-sm mt-2">
                This may take a few moments as we analyze each aspect of the case...
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Results Section - Kanban Board */}
      {analysis && tasks.length > 0 && (
        <div className="mb-8">
          {/* Controls */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Strategic Case Analysis Results
              </h2>
              <p className="text-base text-gray-600 mt-1">
                Total items: {tasks.length} • 
                Last analyzed: {new Date().toLocaleDateString()}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleExportToFile}
                variant="flat"
                color="success"
                size="md"
                isDisabled={!analysis || tasks.length === 0}
              >
                💾 Export Analysis
              </Button>
              <Button
                onClick={handleClear}
                variant="flat"
                color="warning"
                size="md"
              >
                🔄 New Analysis
              </Button>
              <Button
                onClick={analyzeCase}
                variant="flat"
                color="primary"
                size="md"
              >
                🔍 Re-analyze
              </Button>
            </div>
          </div>

          {/* Kanban Board */}
          <div className="flex gap-6 overflow-x-auto pb-6 min-h-[650px]">
            {KANBAN_COLUMNS.map(column => (
              <KanbanColumn
                key={column.id}
                column={column}
                tasks={tasks}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
              />
            ))}
          </div>
        </div>
      )}

      {/* Hidden file input for import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleImportFromFile}
        style={{ display: 'none' }}
        aria-label="Import analysis file"
      />

      {/* How It Works Modal */}
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="3xl" scrollBehavior="inside">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="pb-4">
                <h3 className="text-2xl font-semibold">🕵️ How the Next Steps Strategist Works</h3>
              </ModalHeader>
              <ModalBody className="py-6">
                <div className="space-y-6">
                  <p className="text-gray-700 text-lg">
                    The Next Steps Strategist is an AI-powered detective assistant that analyzes 
                    deputy case reports to provide strategic guidance for follow-up investigation.
                  </p>
                  
                  <div className="grid gap-4">
                    {KANBAN_COLUMNS.map(column => (
                      <div key={column.id} className="flex items-start gap-4 p-4 rounded-lg bg-gray-50">
                        <span className="text-3xl">{column.icon}</span>
                        <div>
                          <h4 className="font-semibold text-gray-900 text-lg">{column.title}</h4>
                          <p className="text-sm text-gray-600 mb-2">{column.subtitle}</p>
                          <p className="text-sm text-gray-500">
                            {column.id === 'completed' && 'Identifies actions already taken by deputies in the initial investigation'}
                            {column.id === 'inProgress' && 'Tracks ongoing investigative work and current status'}
                            {column.id === 'nextSteps' && 'Prioritizes critical next actions for detective follow-up'}
                            {column.id === 'questions' && 'Suggests key questions to ask during detective interviews'}
                            {column.id === 'interviews' && 'Identifies people who should be interviewed by detectives'}
                            {column.id === 'locations' && 'Recommends locations to visit or re-examine'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="bg-blue-50 p-5 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-3 text-lg">🎯 Best Practices for Detectives</h4>
                    <ul className="text-sm text-blue-800 space-y-2">
                      <li>• Include all available case details from deputy reports for comprehensive analysis</li>
                      <li>• Review generated suggestions with your investigative experience</li>
                      <li>• Use priority levels to focus on most critical actions first</li>
                      <li>• Re-analyze as new information becomes available</li>
                      <li>• Remember: AI only extracts from actual reports - no fictional details</li>
                    </ul>
                  </div>
                </div>
              </ModalBody>
              <ModalFooter className="pt-4">
                <Button color="primary" onPress={onClose} size="lg">
                  Got It
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Edit Task Modal */}
      <EditTaskModal
        isOpen={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        task={editingTask}
        onUpdate={handleUpdateTask}
        onCancel={handleCancelEdit}
      />
    </div>
  );
};

export default NextStepsStrategist;